import { RuntimeDefectReporter } from '@st/redux'
import {
  Cause,
  Context,
  Duration,
  Effect,
  Fiber,
  Layer,
  PubSub,
  Queue,
  Result,
  Stream
} from 'effect'

export type RequestSchedule = {
  queue: string
  wait?: Duration.Input
  uniqueKey?: string
}

export type RequestFailureHandling<Message> =
  | { type: 'unhandled' }
  | { type: 'handled'; message?: Message }

export type RequestSchedulerEvent<Response = unknown, Error = unknown, Message = unknown> =
  | { type: 'requestStarted'; requestId: string }
  | { type: 'requestSucceeded'; requestId: string; response: Response; message?: Message }
  | {
      type: 'requestFailed'
      requestId: string
      error: Error
      handling: RequestFailureHandling<Message>
    }

type AnyRequestSchedulerEvent = RequestSchedulerEvent<unknown, unknown, unknown>

type ScheduledJob<Response, Error, Message> = {
  requestId: string
  run: Effect.Effect<Response, Error, never>
  onSucceededMessage?: (response: Response) => Message | null
  recoverFailure?: (error: Error) => Message | null | undefined
}

type AnyScheduledJob = {
  requestId: string
  run: Effect.Effect<unknown, unknown, never>
  onSucceededMessage?: (response: unknown) => unknown | null
  recoverFailure?: (error: unknown) => unknown | null | undefined
}

type DebounceWorker = {
  token: object
  fiber: Fiber.Fiber<void, never>
}

type RequestSchedulerService = {
  events: Stream.Stream<AnyRequestSchedulerEvent>
  schedule: <Response, Error, Message>(args: {
    job: ScheduledJob<Response, Error, Message>
    schedule: RequestSchedule
  }) => Effect.Effect<void>
}

export class RequestScheduler extends Context.Service<RequestScheduler, RequestSchedulerService>()(
  'RequestScheduler'
) {}

export function observeRequestScheduler<
  Response = unknown,
  Error = unknown,
  Message = unknown
>(): Stream.Stream<RequestSchedulerEvent<Response, Error, Message>, never, RequestScheduler> {
  return Stream.unwrap(
    Effect.map(
      RequestScheduler,
      (scheduler) =>
        scheduler.events as Stream.Stream<RequestSchedulerEvent<Response, Error, Message>>
    )
  )
}

export function requestSchedulerLayer() {
  return Layer.effect(
    RequestScheduler,
    Effect.gen(function* () {
      const serviceScope = yield* Effect.scope
      const eventPubSub = yield* PubSub.unbounded<AnyRequestSchedulerEvent>()
      yield* Effect.addFinalizer(() => PubSub.shutdown(eventPubSub))
      const queues = new Map<string, Queue.Queue<AnyScheduledJob>>()
      const debounceWorkers = new Map<string, DebounceWorker>()
      const defectReporter = yield* RuntimeDefectReporter

      const reportCause = (context: string, cause: Cause.Cause<unknown>) => {
        if (Cause.hasInterruptsOnly(cause)) {
          return Effect.void
        }
        return defectReporter.report(context, Cause.squash(cause))
      }

      const emit = (event: AnyRequestSchedulerEvent) =>
        PubSub.publish(eventPubSub, event).pipe(Effect.asVoid)

      const mapMessage = (
        mapper: ((value: unknown) => unknown | null) | undefined,
        value: unknown
      ): Effect.Effect<unknown | undefined> => {
        if (!mapper) {
          return Effect.succeed(undefined)
        }

        return Effect.sync(() => mapper(value)).pipe(Effect.map((message) => message ?? undefined))
      }

      const mapFailure = (
        recover: ((value: unknown) => unknown | null | undefined) | undefined,
        value: unknown
      ): Effect.Effect<RequestFailureHandling<unknown>> => {
        if (!recover) {
          return Effect.succeed({ type: 'unhandled' })
        }

        return Effect.sync(() => recover(value)).pipe(
          Effect.map((message) => {
            if (message == undefined) {
              return { type: 'unhandled' } as const
            }
            if (message == null) {
              return { type: 'handled' } as const
            }
            return { type: 'handled', message } as const
          })
        )
      }

      const runJob = (job: AnyScheduledJob) =>
        Effect.gen(function* () {
          yield* emit({ type: 'requestStarted', requestId: job.requestId })
          const result = yield* Effect.result(job.run)
          if (Result.isSuccess(result)) {
            const message = yield* mapMessage(job.onSucceededMessage, result.success)
            yield* emit({
              type: 'requestSucceeded',
              requestId: job.requestId,
              response: result.success,
              message
            })
            return
          }
          const handling = yield* mapFailure(job.recoverFailure, result.failure)
          yield* emit({
            type: 'requestFailed',
            requestId: job.requestId,
            error: result.failure,
            handling
          })
        })

      // Jobs are independent queue entries. A defect in one is reported and
      // terminates only that entry; the queue continues serving later work.
      const runIsolatedJob = (job: AnyScheduledJob) =>
        runJob(job).pipe(
          Effect.tapCause((cause) =>
            reportCause(`request scheduler job requestId=${job.requestId}`, cause)
          ),
          Effect.exit,
          Effect.asVoid
        )

      const toAnyScheduledJob = <Response, Error, Message>(
        job: ScheduledJob<Response, Error, Message>
      ): AnyScheduledJob => ({
        requestId: job.requestId,
        run: job.run.pipe(
          Effect.map((response): unknown => response),
          Effect.mapError((error): unknown => error)
        ),
        onSucceededMessage:
          job.onSucceededMessage == null
            ? undefined
            : (response) => job.onSucceededMessage?.(response as Response) ?? null,
        recoverFailure:
          job.recoverFailure == null ? undefined : (error) => job.recoverFailure?.(error as Error)
      })

      const debounceWorkerSlotId = (queue: string, uniqueKey: string) => `${queue}:${uniqueKey}`

      const shutdownDebounceWorker = (slotId: string) =>
        Effect.gen(function* () {
          const existing = debounceWorkers.get(slotId)
          if (!existing) return

          yield* Fiber.interrupt(existing.fiber)
          debounceWorkers.delete(slotId)
        })

      const ensureQueue = (queueName: string) =>
        Effect.gen(function* () {
          const existing = queues.get(queueName)
          if (existing) return existing

          const queue = yield* Queue.unbounded<AnyScheduledJob>()
          yield* Stream.fromQueue(queue).pipe(
            Stream.runForEach(runIsolatedJob),
            Effect.tapCause((cause) => reportCause(`request scheduler queue=${queueName}`, cause)),
            Effect.forkIn(serviceScope)
          )
          queues.set(queueName, queue)
          return queue
        })

      const ensureDebounceWorker = (args: {
        slotId: string
        queue: string
        uniqueKey: string
        wait: Duration.Duration
        queueRef: Queue.Queue<AnyScheduledJob>
        job: AnyScheduledJob
      }) =>
        Effect.gen(function* () {
          const existing = debounceWorkers.get(args.slotId)
          if (existing) {
            yield* shutdownDebounceWorker(args.slotId)
          }

          const token = {}
          const clearCurrentWorker = Effect.sync(() => {
            if (debounceWorkers.get(args.slotId)?.token == token) {
              debounceWorkers.delete(args.slotId)
            }
          })
          const fiber = yield* Effect.sleep(args.wait).pipe(
            Effect.andThen(Queue.offer(args.queueRef, args.job)),
            Effect.tapCause((cause) =>
              reportCause(
                `request scheduler debounce worker queue=${args.queue} uniqueKey=${args.uniqueKey}`,
                cause
              )
            ),
            Effect.ensuring(clearCurrentWorker),
            Effect.asVoid,
            Effect.forkIn(serviceScope)
          )

          debounceWorkers.set(args.slotId, {
            token,
            fiber
          })
        })

      const schedule: RequestSchedulerService['schedule'] = ({ job, schedule }) =>
        Effect.gen(function* () {
          const queuedJob = toAnyScheduledJob(job)
          const queue = yield* ensureQueue(schedule.queue)
          const wait = Duration.fromInputUnsafe(schedule.wait ?? 0)
          const uniqueKey = schedule.uniqueKey

          const shouldDebounce =
            uniqueKey != null &&
            uniqueKey != '' &&
            !Duration.isLessThanOrEqualTo(wait, Duration.zero)

          if (!shouldDebounce) {
            if (uniqueKey != null && uniqueKey != '') {
              yield* shutdownDebounceWorker(debounceWorkerSlotId(schedule.queue, uniqueKey))
            }
            yield* Queue.offer(queue, queuedJob)
            return
          }

          const slotId = debounceWorkerSlotId(schedule.queue, uniqueKey)
          yield* ensureDebounceWorker({
            slotId,
            queue: schedule.queue,
            uniqueKey,
            wait,
            queueRef: queue,
            job: queuedJob
          })
          return
        })

      return {
        events: Stream.fromPubSub(eventPubSub),
        schedule
      } satisfies RequestSchedulerService
    })
  )
}
